// Currency-aware payment methods & bank lists.
// The payment section adapts to a chosen "pay in" currency (IDR or the receipt's
// currency). Each currency has one or more methods (bank transfer, PayNow, PayID, etc.).
import { BANK_GROUPS } from "./banks";

const SG_BANKS = ["DBS / POSB", "OCBC Bank", "UOB", "Standard Chartered", "Citibank", "HSBC", "Maybank", "CIMB", "Trust Bank", "GXS Bank", "Bank of China", "Sing Investments & Finance"];
const AU_BANKS = ["Commonwealth Bank (CBA)", "Westpac", "NAB", "ANZ", "Bendigo Bank", "Macquarie Bank", "ING", "Suncorp Bank", "Bankwest", "UBank", "HSBC Australia", "St.George Bank", "Up"];
const TH_BANKS = ["Bangkok Bank", "Kasikornbank (KBank)", "Siam Commercial Bank (SCB)", "Krungthai Bank (KTB)", "Bank of Ayudhya (Krungsri)", "TMBThanachart (ttb)", "Government Savings Bank (GSB)", "Kiatnakin Phatra", "CIMB Thai", "UOB Thailand"];
const MY_BANKS = ["Maybank", "CIMB Bank", "Public Bank", "RHB Bank", "Hong Leong Bank", "AmBank", "Bank Islam", "Bank Rakyat", "OCBC Bank", "HSBC Malaysia", "UOB Malaysia", "Standard Chartered Malaysia", "Affin Bank"];
const PH_BANKS = ["BDO Unibank", "Bank of the Philippine Islands (BPI)", "Metrobank", "Land Bank of the Philippines", "Philippine National Bank (PNB)", "UnionBank", "Security Bank", "RCBC", "China Bank", "EastWest Bank", "GCash", "Maya"];
const US_BANKS = ["Chase", "Bank of America", "Wells Fargo", "Citibank", "U.S. Bank", "Capital One", "PNC Bank", "Truist", "TD Bank", "Ally Bank", "Zelle", "Venmo", "PayPal", "Cash App"];
const GB_BANKS = ["Barclays", "HSBC UK", "Lloyds Bank", "NatWest", "Santander UK", "Halifax", "Nationwide", "TSB", "Monzo", "Starling Bank", "Revolut", "Co-operative Bank"];
const JP_BANKS = ["MUFG Bank", "Sumitomo Mitsui (SMBC)", "Mizuho Bank", "Japan Post Bank (Yucho)", "Resona Bank", "Rakuten Bank", "PayPay Bank", "Sony Bank", "SBI Sumishin Net Bank", "Aeon Bank"];
const KR_BANKS = ["KB Kookmin Bank", "Shinhan Bank", "Woori Bank", "Hana Bank", "NH Nonghyup Bank", "IBK (Industrial Bank of Korea)", "Kakao Bank", "Toss Bank", "K Bank", "Busan Bank", "Standard Chartered Korea", "Citibank Korea"];
const VN_BANKS = ["Vietcombank", "BIDV", "VietinBank", "Agribank", "Techcombank", "MB Bank", "ACB", "VPBank", "Sacombank", "TPBank", "VIB", "MoMo"];
const CN_BANKS = ["ICBC", "China Construction Bank (CCB)", "Agricultural Bank of China (ABC)", "Bank of China (BOC)", "Bank of Communications", "China Merchants Bank", "Alipay", "WeChat Pay"];

const NAME = { key: "account_name", label: "Account holder name", type: "text" };
const ACC = { key: "account_number", label: "Account number", type: "num" };

function bankMethod(banks, extra = {}) {
  return { id: "bank", label: "Bank transfer", banks, fields: [{ key: "bank", label: "Bank", type: "bank" }, ACC, NAME], ...extra };
}

export const PAYMENT_CONFIG = {
  IDR: {
    flag: "🇮🇩",
    methods: [
      {
        id: "bank",
        label: "Bank / e-wallet",
        bankGroups: BANK_GROUPS,
        fields: [{ key: "bank", label: "Bank / e-wallet", type: "bank" }, ACC, NAME],
      },
    ],
  },
  SGD: {
    flag: "🇸🇬",
    methods: [
      { id: "paynow", label: "PayNow", fields: [{ key: "handle", label: "Mobile no. / NRIC / UEN", type: "text" }, { ...NAME, label: "Recipient name" }] },
      bankMethod(SG_BANKS),
    ],
  },
  AUD: {
    flag: "🇦🇺",
    methods: [
      { id: "payid", label: "PayID", fields: [{ key: "handle", label: "PayID (email / mobile)", type: "text" }, { ...NAME, label: "Recipient name" }] },
      { id: "bank", label: "Bank transfer", banks: AU_BANKS, fields: [{ key: "bank", label: "Bank", type: "bank" }, { key: "bsb", label: "BSB", type: "num", placeholder: "e.g. 062-000" }, ACC, NAME] },
    ],
  },
  THB: {
    flag: "🇹🇭",
    methods: [
      { id: "promptpay", label: "PromptPay", fields: [{ key: "handle", label: "Mobile no. / Citizen ID", type: "text" }, { ...NAME, label: "Recipient name" }] },
      bankMethod(TH_BANKS),
    ],
  },
  MYR: {
    flag: "🇲🇾",
    methods: [
      { id: "duitnow", label: "DuitNow", fields: [{ key: "handle", label: "Mobile no. / NRIC / DuitNow ID", type: "text" }, { ...NAME, label: "Recipient name" }] },
      bankMethod(MY_BANKS),
    ],
  },
  PHP: {
    flag: "🇵🇭",
    methods: [
      { id: "gcash", label: "GCash / Maya", fields: [{ key: "handle", label: "Mobile number", type: "text" }, { ...NAME, label: "Recipient name" }] },
      bankMethod(PH_BANKS),
    ],
  },
  USD: { flag: "🇺🇸", methods: [bankMethod(US_BANKS)] },
  GBP: { flag: "🇬🇧", methods: [{ id: "bank", label: "Bank transfer", banks: GB_BANKS, fields: [{ key: "bank", label: "Bank", type: "bank" }, { key: "bsb", label: "Sort code", type: "num", placeholder: "e.g. 20-00-00" }, ACC, NAME] }] },
  EUR: { flag: "🇪🇺", methods: [{ id: "bank", label: "Bank transfer", fields: [{ key: "bank", label: "Bank", type: "text" }, { key: "account_number", label: "IBAN", type: "text" }, NAME] }] },
  JPY: { flag: "🇯🇵", methods: [bankMethod(JP_BANKS)] },
  KRW: { flag: "🇰🇷", methods: [bankMethod(KR_BANKS)] },
  VND: { flag: "🇻🇳", methods: [bankMethod(VN_BANKS)] },
  CNY: { flag: "🇨🇳", methods: [{ id: "bank", label: "Bank / Alipay / WeChat", banks: CN_BANKS, fields: [{ key: "bank", label: "Bank / wallet", type: "bank" }, ACC, NAME] }] },
};

const GENERIC = { flag: "🏦", methods: [{ id: "bank", label: "Bank transfer", fields: [{ key: "bank", label: "Bank", type: "text" }, ACC, NAME] }] };

export function getPayConfig(currency) {
  return PAYMENT_CONFIG[(currency || "IDR").toUpperCase()] || GENERIC;
}

export function getMethod(currency, methodId) {
  const cfg = getPayConfig(currency);
  return cfg.methods.find((m) => m.id === methodId) || cfg.methods[0];
}

// The "pay in" currency options: always IDR, plus the receipt currency if different.
export function payCurrencyOptions(receiptCurrency) {
  const rc = (receiptCurrency || "IDR").toUpperCase();
  return rc === "IDR" ? ["IDR"] : ["IDR", rc];
}

// Turn a stored payment object into a structured, displayable description.
export function describePayment(payment) {
  if (!payment) return null;
  const cur = (payment.pay_currency || "IDR").toUpperCase();
  const method = getMethod(cur, payment.method);
  if (!method) return null;
  const val = (k) => (payment[k] == null ? "" : String(payment[k]).trim());

  const bankField = method.fields.find((f) => f.type === "bank");
  const hasAny = method.fields.some((f) => f.key !== "account_name" && val(f.key));
  if (!hasAny) return null;

  const headline = bankField && val("bank") ? val("bank") : method.label;

  const rows = [];
  method.fields.forEach((f) => {
    if (f.key === "account_name" || f.type === "bank") return;
    const v = val(f.key);
    if (v) rows.push({ key: f.key, label: f.label, value: v });
  });

  const name = val("account_name");
  const copyValue = val("account_number") || val("handle") || (rows[0] ? rows[0].value : "");

  const parts = [headline];
  method.fields.forEach((f) => {
    if (f.type === "bank" || f.key === "account_name") return;
    const v = val(f.key);
    if (!v) return;
    parts.push(f.key === "bsb" ? `${f.label} ${v}` : v);
  });
  if (name) parts.push(name);
  const oneLine = parts.filter(Boolean).join(" · ");

  return { currency: cur, methodId: method.id, methodLabel: method.label, headline, rows, name, copyValue, oneLine };
}

export function paymentLine(payment) {
  const d = describePayment(payment);
  return d ? d.oneLine : null;
}
